// BUILD-042 — Equipment checkout editor (HR Administration, Documents & Signatures), Oct 15 08:13.
//
// The admin has already selected the employee (this panel lives on that employee's page). They add
// equipment rows (type dropdown, description, optional model, optional serial/asset number, notes),
// save a DRAFT and reopen it later, then "Generate & assign" to freeze the list into ONE employee-specific
// PDF and assign it. Only the assigned employee signs (witnesses were removed Oct 15 19:25; the server rejects any witness input).
//
// The generated form follows the Owner-supplied equipment checkout format (Oct 15): the equipment table plus the
// Owner's acknowledgment and return authorization wording, with the employee signature line.
//
// Cycle 12 (FE-3a), Owner-approved layout: "New equipment checkout" is the primary action at the top right; saved drafts ("Draft — not issued") and issued checkouts ("Issued")
// are listed in separate groups; the editor shows "Issue date" at the top right of the draft header, each item as a bordered box ("Item N" with a small "Remove" at its upper
// right; row 1 = Type + Description, row 2 = Model / Serial or asset number / Notes), a compact "+ Add item", and the distinct actions Save draft | Generate & assign | Close draft.
// A draft is NOT issued equipment. A failed save keeps every entered item and shows the server's own message. Nothing here sends anything to a signing provider.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listHrEquipmentCheckouts, saveHrEquipmentCheckoutDraft, discardHrEquipmentCheckoutDraft, generateHrEquipmentCheckout,
  downloadHrDocumentTemplate, type HrEquipmentCheckout, type HrEquipmentItem,
} from '../../../api/client';
import { CardTitle } from './SectionHeading';
import { formatLocalDate } from '../hrFormat';
import { actionErrorMessage, classifyLoadError, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag } from './loadState';
import { DeniedNotice, TargetUnavailableNotice } from './LoadNotices';

interface Row extends HrEquipmentItem { key: string }
let rowSeq = 0;
const newRow = (over: Partial<HrEquipmentItem> = {}): Row => ({
  key: `row-${++rowSeq}`, equipmentType: 'Laptop', description: '', model: '', serialOrAsset: '', notes: '', ...over,
} as Row);

/** Today's date in America/Phoenix as YYYY-MM-DD (the company's calendar), never the browser's zone. */
function todayPhoenix(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

const FIELD = 'border border-gray-300 rounded px-2 py-1.5 text-sm w-full min-w-0 bg-white';
const FIELD_LABEL = 'block min-w-0 text-[11px] text-gray-500';
const SIGNING_STATE: Record<string, string> = {
  awaiting_return: 'Waiting on the employee to sign', awaiting_hr_review: 'Signed, waiting for HR review', needs_correction: 'Needs correction',
  reviewed_complete: 'Reviewed complete', cancelled: 'Cancelled',
};

/** What the embedding screen (guided setup) needs to know about this panel. `list`: 'ready' only after a SUCCESSFUL load; `count` = saved drafts + issued checkouts. */
export interface EquipmentPanelState { list: 'loading' | 'ready' | 'error' | 'blocked'; count: number; editing: boolean; dirty: boolean }

export function EquipmentCheckoutPanel({
  employeeId, callerEmployeeId, onAssigned, onSaved, onStateChange, title = 'Equipment checkout', subtitle, collapsible = true,
}: {
  employeeId: string;
  callerEmployeeId: string | null;
  /** Called after a checkout is generated and assigned, so the documents list refreshes. */
  onAssigned: () => void;
  /** Called after a draft is saved or discarded (the guided-setup summary re-reads). */
  onSaved?: () => void;
  /** Guided setup: the panel's load / editing / unsaved state, so its footer acknowledgment can follow it. */
  onStateChange?: (s: EquipmentPanelState) => void;
  title?: string;
  subtitle?: string;
  /** false: the heading is not a toggle (guided setup shows the card open). */
  collapsible?: boolean;
}) {
  const [checkouts, setCheckouts] = useState<HrEquipmentCheckout[] | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [limits, setLimits] = useState<{ maxItems: number; maxDescription: number; maxModel: number; maxSerialOrAsset: number; maxNotes: number } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [issueDate, setIssueDate] = useState(todayPhoenix());
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<'save' | 'generate' | 'discard' | null>(null);
  const [open, setOpen] = useState(true);
  const [badRows, setBadRows] = useState<Set<string>>(new Set());
  const [confirmClose, setConfirmClose] = useState(false);
  // A synchronous guard: React state updates after the handler returns, so a fast double-click could otherwise start two saves / two generates.
  const inFlight = useRef(false);
  const baseline = useRef('');

  // Oct 23: a FAILED load is its own state. `checkouts` stays null (never loaded) or keeps the last good list; it is never turned into an empty list, so
  // "No equipment checkouts yet." can only mean a load that succeeded and found none.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Oct 24: a REFUSED list (no document-manage permission) is neither "empty" nor an outage. 'denied' = explicit 403; 'target' = the intentionally non-enumerating 404.
  const [blocked, setBlocked] = useState<'denied' | 'target' | null>(null);
  const mayManage = useSurfaceFlag('mayManageDocuments');
  const load = useCallback((): Promise<void> => {
    return listHrEquipmentCheckouts(employeeId)
      .then((d) => { setCheckouts(d.checkouts); setTypes(d.equipmentTypes); setLimits(d.limits); setLoadError(null); setBlocked(null); })
      .catch((e: any) => {
        const view = classifyLoadError(e, { targeted: true });
        if (view.kind === 'denied' || view.kind === 'targetUnavailable') { setBlocked(view.kind === 'denied' ? 'denied' : 'target'); setLoadError(null); return; }
        setBlocked(null);
        setLoadError(view.kind === 'unavailable' && view.message !== 'The request failed.' ? view.message : 'Could not load equipment checkouts.');
      });
  }, [employeeId]);
  useEffect(() => { setEditing(false); setDraftId(null); setMessage(null); setCheckouts(null); setLoadError(null); setBlocked(null); setConfirmClose(false); void load(); }, [employeeId, load]);
  const cannotIssue = mayManage === false;

  const snapshot = JSON.stringify({ issueDate, rows: rows.map((r) => [r.equipmentType, r.description, r.model ?? '', r.serialOrAsset ?? '', r.notes ?? '']) });
  const dirty = editing && snapshot !== baseline.current;
  const drafts = (checkouts ?? []).filter((c) => c.status === 'draft');
  const generated = (checkouts ?? []).filter((c) => c.status === 'generated');

  const onStateRef = useRef(onStateChange);
  onStateRef.current = onStateChange;
  const listState: EquipmentPanelState['list'] = blocked ? 'blocked' : checkouts !== null ? 'ready' : loadError ? 'error' : 'loading';
  useEffect(() => {
    onStateRef.current?.({ list: listState, count: drafts.length + generated.length, editing, dirty });
  }, [listState, drafts.length, generated.length, editing, dirty]);

  const defaultType = types.includes('Laptop') ? 'Laptop' : (types[0] ?? 'Laptop');
  function begin(nextDraftId: string | null, date: string, nextRows: Row[], msg: { kind: 'ok' | 'error'; text: string } | null) {
    setDraftId(nextDraftId); setIssueDate(date); setRows(nextRows); setMessage(msg); setBadRows(new Set()); setConfirmClose(false); setEditing(true);
    baseline.current = JSON.stringify({ issueDate: date, rows: nextRows.map((r) => [r.equipmentType, r.description, r.model ?? '', r.serialOrAsset ?? '', r.notes ?? '']) });
  }
  function startNew() {
    setOpen(true);
    begin(null, todayPhoenix(), [newRow({ equipmentType: defaultType })], null);
  }
  function openDraft(c: HrEquipmentCheckout) {
    setOpen(true);
    // A draft saved before witnesses were removed still carries one on the server; say so, and saving clears it.
    begin(
      c.checkoutId, c.issueDate,
      c.items.length ? c.items.map((i) => newRow({ ...i, model: i.model ?? '', serialOrAsset: i.serialOrAsset ?? '', notes: i.notes ?? '' })) : [newRow({ equipmentType: defaultType })],
      c.witnessEmployeeId ? { kind: 'ok', text: 'This draft was started with a witness, which is no longer used. Save the draft again to clear it, then generate the form.' } : null,
    );
  }
  const update = (key: string, patch: Partial<HrEquipmentItem>) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    if (patch.description !== undefined) setBadRows((s) => { if (!s.has(key)) return s; const n = new Set(s); n.delete(key); return n; });
  };

  function payload() {
    return {
      checkoutId: draftId,
      issueDate,
      items: rows.map((r) => ({
        equipmentType: r.equipmentType, description: r.description.trim(),
        model: (r.model ?? '').trim() || null, serialOrAsset: (r.serialOrAsset ?? '').trim() || null, notes: (r.notes ?? '').trim() || null,
      })),
    };
  }

  /** The same first-line rules the server applies, so a missing description is explained before anything is sent. Entries are never changed. */
  function validate(): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(issueDate)) return 'Choose the issue date.';
    const missing = rows.map((r, i) => ({ r, i })).filter(({ r }) => !r.description.trim());
    if (missing.length) { setBadRows(new Set(missing.map(({ r }) => r.key))); return `Item ${missing[0].i + 1}: description is required.`; }
    setBadRows(new Set());
    return null;
  }

  async function save(): Promise<HrEquipmentCheckout | null> {
    if (inFlight.current) return null;
    const problem = validate();
    if (problem) { setMessage({ kind: 'error', text: `Not saved. ${problem} Your entries are still here.` }); return null; }
    inFlight.current = true;
    setBusy('save'); setMessage(null);
    try {
      const saved = await saveHrEquipmentCheckoutDraft(employeeId, payload());
      setDraftId(saved.checkoutId);
      baseline.current = snapshot;
      setMessage({ kind: 'ok', text: 'Draft saved. It is not issued until you generate and assign it.' });
      await load();
      onSaved?.();
      return saved;
    } catch (e: any) {
      // Every entered item stays on screen; the server's own message is shown (a refusal because the checkout is no longer a draft also refreshes the list).
      setMessage({ kind: 'error', text: `Not saved. ${actionErrorMessage(e, PERMISSION_LABEL.documents, 'save equipment checkouts', 'The draft could not be saved.')} Your entries are still here.` });
      if (e?.status === 409) void load();
      return null;
    } finally { inFlight.current = false; setBusy(null); }
  }

  async function generate() {
    if (inFlight.current) return;
    const saved = await save();
    if (!saved || inFlight.current) return;
    inFlight.current = true;
    setBusy('generate'); setMessage(null);
    try {
      const r = await generateHrEquipmentCheckout(saved.checkoutId);
      setMessage({ kind: 'ok', text: `Generated and assigned (${r.checkout.items.length} item${r.checkout.items.length === 1 ? '' : 's'}). The employee will see it under Needs your action.` });
      setEditing(false); setDraftId(null);
      await load(); onAssigned(); onSaved?.();
    } catch (e: any) {
      setMessage({ kind: 'error', text: `${actionErrorMessage(e, PERMISSION_LABEL.documents, 'generate equipment checkouts', 'Could not generate the checkout form.')} Your draft is saved and nothing was issued.` });
      void load();
    } finally { inFlight.current = false; setBusy(null); }
  }

  async function discard() {
    if (!draftId) { closeEditor(true); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('discard'); setMessage(null);
    try {
      await discardHrEquipmentCheckoutDraft(draftId);
      setEditing(false); setDraftId(null); setMessage({ kind: 'ok', text: 'Draft discarded.' }); await load(); onSaved?.();
    } catch (e: any) {
      setMessage({ kind: 'error', text: e.message || 'Could not discard the draft.' });
    } finally { inFlight.current = false; setBusy(null); }
  }

  /** Close draft: leaves a SAVED draft in place. Unsaved changes are never dropped without asking. */
  function closeEditor(force = false) {
    if (dirty && !force) { setConfirmClose(true); return; }
    setEditing(false); setDraftId(null); setMessage(null); setConfirmClose(false); setBadRows(new Set());
  }

  async function openForm(c: HrEquipmentCheckout) {
    if (!c.assignmentId) return;
    try {
      const { url } = await downloadHrDocumentTemplate(c.assignmentId, 'inline');
      window.open(url, '_blank', 'noopener');
    } catch (e: any) { setMessage({ kind: 'error', text: e.message || 'Could not open the form.' }); }
  }

  const overLimit = limits ? rows.length >= limits.maxItems : false;
  const itemSummary = (c: HrEquipmentCheckout) => c.items.map((i) => i.description).filter(Boolean).join(', ');
  const newDisabled = checkouts === null || editing || cannotIssue || busy !== null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4" data-testid="equipment-checkout-panel">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-3">
        <div className="min-w-0 flex-1 basis-64">
          {collapsible ? (
            <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left" aria-expanded={open}>
              <CardTitle>{title}</CardTitle>
              <span className="text-gray-400 text-xs">{open ? '▾' : '▸'}</span>
            </button>
          ) : (
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          )}
          {open && <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">{subtitle ?? 'List the equipment issued to this employee, save a draft, then generate one checkout form for them to sign.'}</p>}
        </div>
        {/* Nothing is known about existing checkouts until a load succeeds, so a new one is not offered then (it could duplicate an issuance we cannot see). */}
        <button
          type="button" onClick={startNew} disabled={newDisabled}
          title={cannotIssue ? needsPermissionMessage(PERMISSION_LABEL.documents, 'issue equipment') : editing ? 'Finish or close the open draft first.' : checkouts === null ? 'Existing checkouts must load first.' : undefined}
          className="max-w-full ml-auto px-4 py-2 rounded-md bg-[#4AABDE] text-white text-sm font-medium hover:bg-[#3b96c4] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="new-equipment-checkout"
        >
          New equipment checkout
        </button>
      </div>

      {open && (
        <>
          {cannotIssue && <p className="text-xs text-amber-800 mb-3" data-testid="equipment-needs-permission">{needsPermissionMessage(PERMISSION_LABEL.documents, 'issue equipment')}</p>}
          {message && (
            <div role={message.kind === 'error' ? 'alert' : 'status'} className={`text-sm mb-3 ${message.kind === 'error' ? 'text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2' : 'text-green-700'}`} data-testid={message.kind === 'error' ? 'equipment-error' : 'equipment-message'}>{message.text}</div>
          )}

          {blocked === 'denied' && <DeniedNotice thing="equipment checkouts" permission={PERMISSION_LABEL.documents} testId="equipment-denied" />}
          {blocked === 'target' && <TargetUnavailableNotice testId="equipment-target-unavailable" />}
          {loadError && (
            <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" data-testid="equipment-load-error">
              {checkouts === null
                ? <>Equipment checkouts could not be loaded: {loadError.replace(/[.\s]+$/, '')}. <strong>This does not mean no equipment was issued.</strong></>
                : <>The latest refresh failed: {loadError.replace(/[.\s]+$/, '')}. The list below may be out of date.</>}{' '}
              <button type="button" className="underline" onClick={() => void load()}>Try again</button>
            </div>
          )}

          {editing && (
            <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="border border-gray-200 rounded-lg p-4 mb-4 space-y-3" aria-label="Equipment checkout editor" data-testid="equipment-editor">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">Checkout draft</h3>
                  <p className="text-xs text-gray-500">{draftId ? 'Saved as a draft. Not issued yet.' : 'Not saved yet.'}</p>
                </div>
                <label className="text-[11px] text-gray-500 flex flex-col gap-0.5">
                  <span>Issue date</span>
                  <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white" required data-testid="equipment-issue-date" />
                </label>
              </div>

              <div className="space-y-2.5">
                {rows.map((r, i) => (
                  <div key={r.key} role="group" aria-label={`Item ${i + 1}`} className="border border-gray-200 rounded-md p-3 bg-white" data-testid="equipment-row">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-medium text-gray-700">Item {i + 1}</span>
                      {rows.length > 1 && (
                        <button type="button" onClick={() => { setRows((rs) => rs.filter((x) => x.key !== r.key)); setBadRows((s) => { const n = new Set(s); n.delete(r.key); return n; }); }} aria-label={`Remove item ${i + 1}`} className="text-[11px] text-red-700 hover:underline" data-testid="equipment-remove-item">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[11rem_minmax(0,1fr)] gap-2 mb-2">
                      <label className={FIELD_LABEL}>Type
                        <select value={r.equipmentType} onChange={(e) => update(r.key, { equipmentType: e.target.value })} className={FIELD}>
                          {(types.length ? types : [r.equipmentType]).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className={FIELD_LABEL}>Description (required)
                        <input type="text" value={r.description} maxLength={limits?.maxDescription} onChange={(e) => update(r.key, { description: e.target.value })} aria-required="true" aria-invalid={badRows.has(r.key) || undefined}
                          className={`${FIELD} ${badRows.has(r.key) ? 'border-red-500' : ''}`} placeholder="e.g. Dell Latitude 5440" data-testid="equipment-description" />
                      </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <label className={FIELD_LABEL}>Model (optional)
                        <input type="text" value={r.model ?? ''} maxLength={limits?.maxModel} onChange={(e) => update(r.key, { model: e.target.value })} className={FIELD} />
                      </label>
                      <label className={FIELD_LABEL}>Serial / asset number (optional)
                        <input type="text" value={r.serialOrAsset ?? ''} maxLength={limits?.maxSerialOrAsset} onChange={(e) => update(r.key, { serialOrAsset: e.target.value })} className={FIELD} />
                      </label>
                      <label className={FIELD_LABEL}>Notes (optional)
                        <input type="text" value={r.notes ?? ''} maxLength={limits?.maxNotes} onChange={(e) => update(r.key, { notes: e.target.value })} className={FIELD} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <button type="button" onClick={() => setRows((rs) => [...rs, newRow({ equipmentType: defaultType })])} disabled={overLimit} className="text-sm text-[#176A9B] hover:underline font-medium disabled:opacity-50 disabled:no-underline" data-testid="add-equipment-row">
                  + Add item
                </button>
                {overLimit && limits && <span className="text-[11px] text-gray-500 ml-2">A checkout can list at most {limits.maxItems} items.</span>}
              </div>

              {confirmClose && (
                <div role="alertdialog" aria-label="Unsaved changes" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2" data-testid="equipment-close-confirm">
                  <span>This draft has changes that are not saved. Closing it now loses them.</span>
                  <span className="flex gap-3">
                    <button type="button" className="text-sm text-gray-700 underline" onClick={() => setConfirmClose(false)}>Keep editing</button>
                    <button type="button" className="text-sm text-red-700 underline" onClick={() => closeEditor(true)} data-testid="equipment-close-anyway">Close without saving</button>
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-x-3 gap-y-2 items-center pt-1">
                <button type="button" onClick={() => void save()} disabled={busy !== null} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="save-equipment-draft">
                  {busy === 'save' ? 'Saving…' : 'Save draft'}
                </button>
                <button type="button" onClick={() => void generate()} disabled={busy !== null} className="px-4 py-2 rounded-md bg-[#4AABDE] text-white text-sm font-medium hover:bg-[#3b96c4] disabled:opacity-50" data-testid="generate-equipment-checkout">
                  {busy === 'generate' ? 'Generating…' : 'Generate & assign'}
                </button>
                <button type="button" onClick={() => closeEditor()} disabled={busy !== null} className="text-sm text-[#176A9B] hover:underline disabled:opacity-50" data-testid="close-equipment-draft">Close draft</button>
                {draftId && (
                  <button type="button" onClick={() => void discard()} disabled={busy !== null} className="text-sm text-red-700 hover:underline ml-auto disabled:opacity-50" data-testid="discard-equipment-draft">Discard draft</button>
                )}
              </div>
              <p className="text-[11px] text-gray-500">Generate &amp; assign freezes this list and gives the employee one form to sign; after that it cannot be edited. A saved draft is not issued equipment.</p>
            </form>
          )}

          {checkouts === null ? (
            (loadError || blocked) ? null : <div className="text-xs text-gray-400" data-testid="equipment-loading">Loading…</div>
          ) : (
            <>
              {drafts.length > 0 && (
                <section className="mb-3" aria-label="Draft checkouts, not issued" data-testid="equipment-drafts">
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Draft — not issued</h4>
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                    {drafts.map((c) => (
                      <li key={c.checkoutId} className="px-3 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm" data-testid="equipment-draft-row">
                        <span className="min-w-0 flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-medium text-amber-900 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">Not issued</span>
                          <span className="min-w-0 break-words">{c.items.length} item{c.items.length === 1 ? '' : 's'} · issue date {formatLocalDate(c.issueDate)}{itemSummary(c) ? <span className="text-gray-500"> · {itemSummary(c)}</span> : null}</span>
                        </span>
                        <button type="button" onClick={() => openDraft(c)} disabled={editing} title={editing ? 'Close the open draft first.' : undefined} className="text-sm text-[#176A9B] hover:underline font-medium disabled:opacity-50 disabled:no-underline" data-testid="equipment-open-draft">
                          {editing && c.checkoutId === draftId ? 'Open in editor' : 'Open draft'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {generated.length > 0 && (
                <section aria-label="Issued checkouts" data-testid="equipment-issued">
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Issued</h4>
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                    {generated.map((c) => (
                      <li key={c.checkoutId} className="px-3 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm" data-testid="equipment-issued-row">
                        <span className="min-w-0 flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">Issued</span>
                          <span className="min-w-0 break-words">{c.items.length} item{c.items.length === 1 ? '' : 's'} · issued {formatLocalDate(c.issueDate)} · {c.assignmentStatus ? (SIGNING_STATE[c.assignmentStatus] ?? c.assignmentStatus.replace(/_/g, ' ')) : 'assigned'}{c.witnessEmployeeId ? ' (issued with a witness line)' : ''}</span>
                        </span>
                        <button type="button" onClick={() => void openForm(c)} className="text-sm text-[#176A9B] hover:underline font-medium">View form</button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {drafts.length === 0 && generated.length === 0 && !editing && <div className="text-sm text-gray-500" data-testid="equipment-none">No equipment checkouts yet.</div>}
            </>
          )}
        </>
      )}
    </div>
  );
}
